/**
 * SuggestionsPanel.jsx (admins, Mine view): to-dos the portal suggests from the
 * records (overdue invoices, quiet quotes, stuck jobs, web leads). Add makes it
 * a to-do for me, due today; Dismiss hides it for good.
 *
 * "Suggested (n)" sits above the list. More than 3: collapsed to the header at
 * first; open, it shows the 3 oldest plus "Show all". Hidden when there are
 * none. A failed load says so (it must never read as "nothing suggested").
 *
 * Titles and links are record text: every control has a static data-track.
 */
import { useState } from 'react';
import { Box, Paper, Typography, Button, IconButton, Tooltip, Alert, Collapse } from '@mui/material';
import {
  Close as DismissIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Lightbulb as SuggestIcon,
} from '@mui/icons-material';
import TodoLinkChip from './TodoLinkChip';

const FIRST = 3;

export default function SuggestionsPanel({ query, busyKey, onAccept, onDismiss }) {
  const items = query.data?.items || [];
  const [open, setOpen] = useState(null); // null until toggled: open when there are 3 or fewer
  const [showAll, setShowAll] = useState(false);

  if (query.isError && !query.data) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={<Button color="inherit" size="small" onClick={() => query.refetch()} sx={{ textTransform: 'none' }}>Try again</Button>}
      >
        Could not load suggestions.
      </Alert>
    );
  }
  if (!items.length) return null;

  const isOpen = open ?? items.length <= FIRST;
  const shown = showAll ? items : items.slice(0, FIRST);

  return (
    <Paper variant="outlined" sx={{ mb: 2, px: 1, py: 0.5, borderColor: 'warning.light' }}>
      <Box
        data-track="todos.suggestion.toggle"
        onClick={() => setOpen(!isOpen)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 44, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
      >
        {isOpen ? <ArrowDownIcon sx={{ fontSize: 20 }} /> : <ArrowRightIcon sx={{ fontSize: 20 }} />}
        <SuggestIcon sx={{ fontSize: 18, color: 'warning.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Suggested ({items.length})</Typography>
      </Box>
      <Collapse in={isOpen}>
        {shown.map((s) => {
          const busy = busyKey === s.key;
          return (
            <Box
              key={s.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, pl: 1, borderTop: 1, borderColor: 'divider' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{s.title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, mt: 0.25 }}>
                  {s.detail && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.detail}</Typography>}
                  {s.link && <TodoLinkChip link={s.link} trackKey="todos.suggestion.link" />}
                </Box>
              </Box>
              <Button
                data-track="todos.suggestion.add"
                variant="outlined"
                size="small"
                onClick={() => onAccept(s)}
                disabled={!!busyKey}
                sx={{ textTransform: 'none', minHeight: 44, minWidth: 0, px: 1.5, flexShrink: 0 }}
              >
                {busy ? 'Adding...' : 'Add'}
              </Button>
              <Tooltip title="Dismiss">
                <span>
                  <IconButton
                    data-track="todos.suggestion.dismiss"
                    aria-label="Dismiss"
                    onClick={() => onDismiss(s)}
                    disabled={!!busyKey}
                    sx={{ width: 44, height: 44 }}
                  >
                    <DismissIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
        {items.length > FIRST && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', py: 0.5 }}>
            <Button
              data-track={showAll ? 'todos.suggestion.showFewer' : 'todos.suggestion.showAll'}
              size="small"
              onClick={() => setShowAll(!showAll)}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              {showAll ? 'Show fewer' : `Show all ${items.length}`}
            </Button>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
